import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { ChevronDown } from '@outdoor-voices/ui';
import { Image } from '@outdoor-voices/ui';
import { KEY_CODES, KEY_DOWN, RESIZE, SCROLL } from '@outdoor-voices/ui/dist/config/constants';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from 'urql';
import cc from 'classcat';
import PropTypes from 'prop-types';
import Button from '../Button';
import clickEvent from '../../utils/dom/clickEvent';
import css from './SelectPalette.module.scss';
import cssSelect from '../Select/Select.module.scss';
import isVisible from '../../utils/dom/isVisible';
import Options from './Options';
import toGlobalID from '../../utils/toGlobalID';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var getOption = function getOption(options, value) {
  return options.find(function (option) {
    return toGlobalID(option.id) === value;
  });
};
/* eslint-disable react-hooks/exhaustive-deps */


var SelectPalette = function SelectPalette(_ref) {
  var _data$options, _data$options$edges;

  var ariaLabel = _ref.ariaLabel,
      buttonProps = _ref.buttonProps,
      disabled = _ref.disabled,
      hideLabel = _ref.hideLabel,
      label = _ref.label,
      name = _ref.name,
      onChange = _ref.onChange,
      placeholder = _ref.placeholder,
      query = _ref.query,
      variables = _ref.variables,
      value = _ref.value;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      hovered = _useState4[0],
      setHovered = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      interacted = _useState6[0],
      setInteracted = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      open = _useState8[0],
      setOpen = _useState8[1];

  var _useState9 = useState({
    flip: false,
    left: 0,
    top: 0
  }),
      _useState10 = _slicedToArray(_useState9, 2),
      position = _useState10[0],
      setPosition = _useState10[1];

  var _useQuery = useQuery({
    query: query,
    variables: variables
  }),
      _useQuery2 = _slicedToArray(_useQuery, 1),
      _useQuery2$ = _useQuery2[0],
      data = _useQuery2$.data,
      fetching = _useQuery2$.fetching,
      error = _useQuery2$.error;

  var buttonRef = useRef(null);
  var interval = useRef(null);
  var optionsRef = useRef(null);
  var getPosition = useCallback(function () {
    var buttonEl = buttonRef.current;
    var optionsEl = optionsRef.current;
    var tar = 12;

    var _buttonEl$getBounding = buttonEl.getBoundingClientRect(),
        buttonLeft = _buttonEl$getBounding.left,
        buttonTop = _buttonEl$getBounding.top;

    var buttonWidth = buttonEl.offsetWidth;
    var buttonHeight = buttonEl.offsetHeight;
    var optionsWidth = optionsEl.offsetWidth;
    var optionsHeight = optionsEl.offsetHeight;
    var flip = buttonTop + buttonHeight + optionsEl.offsetHeight > window.innerHeight;
    var optionsLeft = buttonLeft + buttonWidth / 2 - optionsWidth / 2;
    var optionsTop = flip ? buttonTop - optionsHeight - 12 : buttonTop + buttonHeight + 12;
    var left = Math.round(Math.max(tar, Math.min(optionsLeft, window.innerWidth - optionsWidth - tar)));
    var top = Math.max(Math.min(optionsTop, window.innerHeight - optionsHeight), 0);
    return {
      flip: flip,
      left: left,
      top: top
    };
  }, [buttonRef, optionsRef]);

  var focus = function focus() {
    var current = optionsRef.current;
    var selected = current.querySelector('[aria-selected="true"]');
    var target = selected || current.querySelector('button:not(:disabled)');
    target.focus();
  };

  var options = (data === null || data === void 0 ? void 0 : (_data$options = data.options) === null || _data$options === void 0 ? void 0 : (_data$options$edges = _data$options.edges) === null || _data$options$edges === void 0 ? void 0 : _data$options$edges.map(function (_ref2) {
    var node = _ref2.node;
    return _objectSpread(_objectSpread({}, node), {}, {
      ariaLabel: "Click to filter by ".concat(node.title),
      value: toGlobalID(node.id)
    });
  })) || [];

  var close = function close() {
    setOpen(false);
  };

  var doOpen = function doOpen() {
    setInteracted(true);
    setPosition(getPosition());
    setOpen(true);
  };

  var select = function select(newValue) {
    onChange({
      target: {
        name: name,
        value: newValue,
        label: label
      }
    });
    close();
  };

  var toggle = function toggle() {
    if (open) {
      close();
    } else {
      doOpen();
    }
  };

  var getLabel = function getLabel(opts) {
    if (fetching || value === false || value === '') {
      return placeholder;
    }

    var selectedOption = getOption(opts, value);
    return /*#__PURE__*/_jsxs("div", {
      className: css.imgLabel,
      children: [/*#__PURE__*/_jsx("div", {
        className: css.img,
        children: /*#__PURE__*/_jsx(Image, _objectSpread(_objectSpread({}, selectedOption.image), {}, {
          alt: "".concat(selectedOption.title, " image"),
          sizes: [18],
          wrap: false
        }))
      }), selectedOption.title]
    });
  };

  var onFocus = function onFocus() {
    setFocused(true);
    setHovered(true);
  };

  var onBlur = function onBlur() {
    setFocused(false);
  };

  useEffect(function () {
    var onKeyDownFocused = function onKeyDownFocused(event) {
      switch (event.key) {
        case KEY_CODES.ARROW_DOWN:
        case KEY_CODES.ARROW_UP:
          event.preventDefault();
          setPosition(getPosition());
          setOpen(true);
          break;

        default:
          break;
      }
    };

    if (focused) {
      document.addEventListener(KEY_DOWN, onKeyDownFocused, false);
    } else {
      document.removeEventListener(KEY_DOWN, onKeyDownFocused);
    }

    return function () {
      document.removeEventListener(KEY_DOWN, onKeyDownFocused);
    };
  }, [focused, getPosition]);
  useEffect(function () {
    var current = buttonRef.current;

    var doClose = function doClose() {
      setOpen(false);
    };

    var focusOption = function focusOption(dir) {
      var current = optionsRef.current;
      var buttons = current.querySelectorAll('button:not(:disabled)');
      var items = Array.from(buttons);
      var index = Math.max(0, Math.min(items.indexOf(document.activeElement) + dir, items.length - 1));
      items[index].focus();
    };

    var onDraw = function onDraw() {
      if (!isVisible(current)) {
        setOpen(false);
        return;
      }

      setPosition(getPosition());
    };

    var onKeyDown = function onKeyDown(event) {
      var ARROW_DOWN = KEY_CODES.ARROW_DOWN,
          ARROW_LEFT = KEY_CODES.ARROW_LEFT,
          ARROW_RIGHT = KEY_CODES.ARROW_RIGHT,
          ARROW_UP = KEY_CODES.ARROW_UP,
          ESC = KEY_CODES.ESC,
          TAB = KEY_CODES.TAB;

      switch (event.key) {
        case ESC:
          doClose();
          break;

        case ARROW_LEFT:
        case ARROW_UP:
          event.preventDefault();
          focusOption(-1);
          break;

        case ARROW_DOWN:
        case ARROW_RIGHT:
          event.preventDefault();
          focusOption(1);
          break;

        case TAB:
          event.preventDefault();
          break;

        default:
          break;
      }
    };

    var clearListeners = function clearListeners() {
      var _document$querySelect;

      document.removeEventListener(KEY_DOWN, onKeyDown);
      document.removeEventListener(SCROLL, onDraw);
      (_document$querySelect = document.querySelector('html')) === null || _document$querySelect === void 0 ? void 0 : _document$querySelect.removeEventListener(clickEvent(), doClose);
      window.removeEventListener(RESIZE, onDraw);
    };

    if (open) {
      document.addEventListener(KEY_DOWN, onKeyDown, false);
      document.addEventListener(SCROLL, onDraw, {
        passive: true
      });
      window.addEventListener(RESIZE, onDraw, {
        passive: true
      });
      interval.current = setTimeout(function () {
        var _document$querySelect2;

        (_document$querySelect2 = document.querySelector('html')) === null || _document$querySelect2 === void 0 ? void 0 : _document$querySelect2.addEventListener(clickEvent(), doClose, false);
        focus();
      }, 150);
    } else if (interacted) {
      clearListeners();

      if (current) {
        current.querySelector('button').focus();
      }
    }

    return function () {
      clearInterval(interval.current);
      clearListeners();
    };
  }, [open, interacted, optionsRef, buttonRef, getPosition]);

  if (error) {
    return false;
  }

  return /*#__PURE__*/_jsxs("div", {
    className: cc([cssSelect.root, _defineProperty({}, cssSelect.open, open)]),
    children: [!hideLabel && /*#__PURE__*/_jsx("button", {
      className: cc([css.label]),
      onClick: doOpen,
      type: "button",
      children: label
    }), /*#__PURE__*/_jsx("div", {
      ref: buttonRef,
      children: /*#__PURE__*/_jsx(Button, _objectSpread(_objectSpread({}, buttonProps), {}, {
        "aria-busy": fetching,
        "aria-expanded": open,
        "aria-haspopup": "listbox",
        "aria-label": ariaLabel || label,
        disabled: disabled,
        iconRight: /*#__PURE__*/_jsx(ChevronDown, {}),
        onBlur: onBlur,
        onClick: toggle,
        onFocus: onFocus,
        onMouseEnter: function onMouseEnter() {
          return setHovered(true);
        },
        role: "combobox",
        type: "button",
        title: ariaLabel || label,
        children: getLabel(options)
      }))
    }), !fetching && data && hovered && /*#__PURE__*/_jsx(Options, {
      left: position.left,
      onChange: select,
      open: open,
      options: options,
      ref: optionsRef,
      top: position.top,
      value: value
    })]
  });
};

SelectPalette.defaultProps = {
  ariaLabel: false,
  buttonProps: {
    gray: true
  },
  hideLabel: true,
  disabled: false,
  placeholder: 'Select',
  value: false
};
SelectPalette.propTypes = {
  ariaLabel: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  buttonProps: PropTypes.shape(),
  disabled: PropTypes.bool,
  hideLabel: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  query: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool, PropTypes.number, PropTypes.string]),
  variables: PropTypes.shape().isRequired
};
export default /*#__PURE__*/memo(SelectPalette);